import {
  getHeaderImage,
  PINWHEEL_LOGO_D6D5C9,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  getSessionDateLabel,
  getSessionTimeLabel,
  formatDateParts,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import ContentEditable from './content-editable';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import FooterWidget from './widgets/footer-widget';
import LogoWidget from './widgets/logo-widget';
import { templateStaticDefaults } from './template-defaults';
import { BlockRenderer } from './widgets/block-renderer';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';
import { BLOCK_ID } from './widgets/block-ids';

const DEFAULTS = templateStaticDefaults.amalfi;

const AMALFI_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.headerLogo]: { height: 40, padding: '24px', background: 'var(--accent)' },
  [BLOCK_ID.salutation]: {
    color: '#D6D5C9',
    fontFamily: 'var(--heading-font)',
    fontSize: '72px',
    fontWeight: 700,
    lineHeight: '58px',
    whiteSpace: 'pre-line',
    background: 'var(--accent)',
    padding: '20px 56px 56px 56px',
  },
  [BLOCK_ID.description]: {
    color: 'var(--body)',
    fontFamily: 'var(--body-font)',
    fontSize: '20px',
    lineHeight: '28px',
    padding: '32px 56px',
  },
  [BLOCK_ID.mainImage]: {
    width: 488,
    height: 286,
    padding: '0 56px 12px 56px',
    borderRadius: 'var(--image-radius)',
  },
  [BLOCK_ID.dateTimeCard]: {
    accentColor: 'var(--accent)',
    headingColor: 'var(--body)',
    labelFontWeight: 700,
    cardBorderRadius: '0',
    borderColor: 'var(--accent)',
    dividerColor: 'rgba(10,16,13,0.1)',
    padding: '12px 56px',
  },
  [BLOCK_ID.cta]: {
    fullWidth: true,
    height: '72px',
    buttonPadding: '0',
    fontSize: '18px',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--heading-font)',
    fontWeight: 700,
    padding: '20px 56px',
  },
  [BLOCK_ID.legalText]: {
    color: '#D6D5C9',
    fontFamily: 'var(--body-font)',
    fontSize: '12px',
    lineHeight: '18px',
    textAlign: 'center',
    opacity: 0.7,
    background: 'var(--secondary-bg)',
    padding: '48px 64px',
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

export const Amalfi = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const secondaryBg = resolveThemedColor(
    customizations?.secondaryBackgroundColor,
    themeColors?.secondaryBackgroundColor,
    DEFAULTS.secondaryBackgroundColor
  );
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '0px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '0px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || PINWHEEL_LOGO_D6D5C9;
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Tilt Warp", Arial Black, sans-serif'
  );
  const bodyFont = resolveThemedFont(customizations?.bodyFont, theme?.font_pack?.body_name, 'Inter, Arial, sans-serif');
  const _salutation = salutation ?? (customizations?.salutation || DEFAULTS.salutation);
  const eventDescription = description || customizations?.description || DEFAULTS.description;
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = body;
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      secondaryBg,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: AMALFI_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <>
        <table
          data-component="Amalfi"
          width="100%"
          cellPadding={0}
          cellSpacing={0}
          border={0}
          role="presentation"
          className={styles.email}
          style={
            {
              '--bg': bg,
              '--container': container,
              '--accent': accent,
              '--button-color': buttonColor,
              '--secondary-bg': secondaryBg,
              '--heading': heading,
              '--button-text': buttonText,
              '--body': body,
              '--heading-font': headingFont,
              '--body-font': bodyFont,
              '--image-radius': imageRadius,
              '--button-radius': buttonRadius,
              background: 'var(--bg)',
            } as React.CSSProperties
          }
        >
          <tbody>
            <tr>
              <td align="center" style={{ padding: '0 32px' }}>
                <table
                  width={600}
                  cellPadding={0}
                  cellSpacing={0}
                  border={0}
                  role="presentation"
                  style={{ maxWidth: '600px' }}
                >
                  <tbody>
                    <tr>
                      <td style={{ background: 'var(--container)' }}>
                        {blocks.map((block) => (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === blocks[0]?.id}
                            isLast={block.id === blocks[blocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={block}
                              stylePreset={AMALFI_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        ))}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table
                  width={600}
                  cellPadding={0}
                  cellSpacing={0}
                  border={0}
                  role="presentation"
                  style={{ maxWidth: '600px' }}
                >
                  <tbody>
                    {!!calendarLinks && (
                      <tr>
                        <td align="center" style={{ padding: '24px 0' }}>
                          <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </>
    );
  }

  return (
    <table
      data-component="Amalfi"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--secondary-bg': secondaryBg,
          '--heading': heading,
          '--button-text': buttonText,
          '--body': body,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                <tr>
                  <td style={{ background: 'var(--accent)' }}>
                    <LogoWidget src={headerImg} setImage={setHeaderImage} height={40} padding="24px" />

                    <HeaderWidget
                      value={_salutation}
                      setValue={setSalutation}
                      color="#D6D5C9"
                      fontFamily="var(--heading-font)"
                      fontSize="72px"
                      fontWeight={700}
                      lineHeight="58px"
                      whiteSpace="pre-line"
                      padding="20px 56px 56px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ background: 'var(--container)' }}>
                    <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td>
                            <ParagraphWidget
                              value={eventDescription}
                              setValue={setDescription}
                              color="var(--body)"
                              fontFamily="var(--body-font)"
                              fontSize="20px"
                              lineHeight="28px"
                              padding="32px 56px"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <ImageWidget
                              src={bodyImg}
                              setImage={setMainImage}
                              width={488}
                              height={286}
                              padding="0 56px 12px 56px"
                              borderRadius={imageRadius}
                            />
                          </td>
                        </tr>
                        {sessionTimestamp && (
                          <tr>
                            <td style={{ padding: '12px 56px' }}>
                              <table
                                width="100%"
                                cellPadding={0}
                                cellSpacing={0}
                                border={0}
                                role="presentation"
                                style={{ border: '1px solid var(--accent)' }}
                              >
                                <tbody>
                                  <tr>
                                    <td style={{ padding: '20px', borderBottom: '1px solid rgba(10,16,13,0.1)' }}>
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              style={{
                                                color: 'var(--accent)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 700,
                                              }}
                                            >
                                              Date
                                            </td>
                                            <td
                                              align="right"
                                              style={{
                                                color: 'var(--body)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 700,
                                              }}
                                            >
                                              {sessionDateLabel}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td style={{ padding: '20px' }}>
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              style={{
                                                color: 'var(--accent)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 700,
                                              }}
                                            >
                                              Time
                                            </td>
                                            <td
                                              align="right"
                                              style={{
                                                color: 'var(--body)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 700,
                                              }}
                                            >
                                              {sessionTimeLabel}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ padding: '20px 56px' }}>
                            <table width="100%" cellPadding={0} cellSpacing={0} border={0} role="presentation">
                              <tbody>
                                <tr>
                                  <td
                                    align="center"
                                    valign="middle"
                                    style={{
                                      background: 'var(--button-color)',
                                      height: '72px',
                                      borderRadius: buttonRadius,
                                    }}
                                  >
                                    <a
                                      href={anchorHref}
                                      onClick={(e) => e.preventDefault()}
                                      style={{
                                        textDecoration: 'none',
                                        display: 'inline-block',
                                        height: '100%',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <ContentEditable
                                        value={_cta}
                                        setValue={setCta}
                                        style={{
                                          color: 'var(--button-text)',
                                          fontSize: '18px',
                                          fontFamily: 'var(--heading-font)',
                                          fontWeight: 700,
                                          lineHeight: '72px',
                                          wordWrap: 'break-word',
                                          whiteSpace: 'nowrap',
                                        }}
                                      />
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ background: 'var(--secondary-bg)', padding: '48px 64px' }}>
                    <div style={{ opacity: 0.7 }}>
                      <ParagraphWidget
                        value={DEFAULTS.complianceText}
                        color="#D6D5C9"
                        fontFamily="var(--body-font)"
                        fontSize="12px"
                        lineHeight="18px"
                        textAlign="center"
                        padding="0"
                      />
                    </div>
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
